export interface CallQueue {
  id: string
  name: string
  extensionNumber: string
}

export interface ApiQueueItem {
  id: number
  name: string
  number: string
}

export interface PaginatedQueuesResponse {
  count: number
  next: string | null
  previous: string | null
  results: ApiQueueItem[]
}

export type CallerState = 'Waiting' | 'Trying' | 'Answered' | 'Abandoned'

export interface QueueCaller {
  uuid: string
  cidNumber: string
  cidName: string
  callerNumber: string
  state: CallerState
  score: number
  joinedEpoch: number
  bridgeEpoch: number
}

export const CALLER_STATE_DISPLAY: Record<CallerState, string> = {
  Trying: 'Waiting',
  Waiting: 'Waiting',
  Answered: 'Answered',
  Abandoned: 'Cancelled',
}

export type AgentStatus =
  'available' | 'available_on_demand' | 'logged_out' | 'on_break'

export type AgentState = 'idle' | 'waiting' | 'receiving' | 'in_a_queue_call'

export interface QueueAgent {
  agent: string
  name: string
  extension: string
  status: AgentStatus
  state: AgentState
  lastStatusChange: number
  lastOfferedCall: number
  callsAnswered: number
  noAnswerCount: number
  talkTime: number
}

export const AGENT_STATE_DISPLAY: Record<AgentState, string> = {
  idle: 'Ready',
  waiting: 'Ready',
  receiving: 'Ringing',
  in_a_queue_call: 'On Call',
}

/** Raw response shape from the `/app/v1/metrics/queue` endpoint. */
export interface QueueMetricsApiResponse {
  data: {
    total_calls: number
    total_answered: number
    total_canceled: number
    avg_wait_seconds: number
    avg_cancel_seconds: number
    max_wait_seconds: number
    max_cancel_seconds: number
  }
}

export interface QueueStats {
  answeredCount: number
  cancelledCount: number
  avgWaitSeconds: number
  maxWaitSeconds: number
  avgCancelledSeconds: number
  maxCancelledSeconds: number
  dropRatePercent: number
}

export interface QueueDashboardSettings {
  refreshInterval: number
  holdAlertThreshold: number
  sampleHistoryMinutes: number
}

export interface QueueCallsResponse {
  calls: QueueCaller[]
}

export interface QueueAgentsResponse {
  activeAgents: QueueAgent[]
  inactiveAgents: QueueAgent[]
}

/** Domain-level stats transformed from QueueMetricsApiResponse by fetchQueueStats. */
export interface QueueStatsResponse {
  stats: QueueStats
}

export interface AgentActionResponse {
  success: boolean
}

export interface QueueDashboardCustomStatus {
  id: string
  name: string
  description?: string
  availability: 'Online' | 'Away'
  pbx_id?: number
}

export interface QueueDashboardExtension {
  id: number
  uuid?: string
}

export const enum QueueAgentActionStatus {
  InQueue = 'IN_QUEUE',
  Offline = 'OFFLINE',
}

export interface QueueDashboardAgentActions {
  onQueueLogin?: (extensionId: number) => Promise<void>
  onQueueLogout?: (extensionId: number) => Promise<void>
  onUpdateAgentStatus?: (
    customStatusId: string | null,
    extUuid?: string,
    inQueue?: boolean,
  ) => Promise<void>
  onQueueUpdateError?: (error: unknown) => void
  onStatusUpdated?: () => void
}
